import Link, { type LinkProps } from 'next/link'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { type TextLinkProps } from '@/ui/atoms/text-link'

import { LinkCustomExternalStyled, LinkCustomStyled, LinkTargetDescription } from './styled'

// Omit href from TextLinkProps as we want to use the href prop from Next.js Link
type OmittedTextLinkProps = Omit<TextLinkProps, 'href'>

// Exclude target="_blank" from TextLinkProps
type ExcludeTargetBlank<T> = T extends { target?: '_blank' | infer R } ? T & { target?: R } : T

// Exclude passHref and legacyBehavior from LinkProps
type CustomLinkProps = Omit<LinkProps, 'passHref' | 'legacyBehavior'>

export type BaseLinkCustomProps = ExcludeTargetBlank<
  OmittedTextLinkProps & {
    /**
     * The URL the link should point to. If wrap by `<Trans`, use the `noChildren` prop.
     */
    href: string
    /**
     * Whether the link should be rendered as a button.
     */
    button?: boolean
    /**
     * Whether the link should open in a new tab (adds `target="_blank"` and `rel="noopener noreferrer"`)
     */
    openInNewTab?: boolean
    /**
     * Whether the link should be full width (used when it's a button)
     */
    isFullWidth?: boolean
  } & Partial<CustomLinkProps>
>

type LinkCustomPropsWithChildren = BaseLinkCustomProps & {
  noChildren?: false
  children: React.ReactNode
}

type LinkCustomPropsWithoutChildren = BaseLinkCustomProps & {
  /**
   * Used when children are passed as props to the component. Example is when wrapping with <Trans.
   */
  noChildren: true
  children?: React.ReactNode
}

export type LinkCustomProps = LinkCustomPropsWithChildren | LinkCustomPropsWithoutChildren

/**
 * `LinkCustom` is a component that renders a link, which can be either internal or external. Legacy pages on kijiji.ca should be considered external as they are not part of the Nextjs app and should not be using the Link from Nextjs.
 *
 * The component decides whether to render an external or internal link based on the `href` prop. External links (those starting with 'http', 'https', or '//') are rendered with `LinkCustomExternalStyled`, while internal links are handled using Next.js's `Link` component with `LinkCustomStyled`.
 *
 * The component uses `React.forwardRef` to forward a ref to the underlying anchor element.
 *
 * @param props - Props for the LinkCustom component.
 * @param ref - Ref forwarded to the anchor element.
 * @returns A styled link element based on the provided props.
 *
 * @example
 * <LinkCustom href="/about" button={true}>
 *   About Us
 * </LinkCustom>
 */

const LinkCustom = React.forwardRef<HTMLAnchorElement, LinkCustomProps>(
  ({ href, openInNewTab, children, noChildren, ...rest }, ref) => {
    const { t } = useTranslation('common')

    // Check if the link is an external link
    const isExternalLink =
      typeof href === 'string' &&
      (href.startsWith('http') || href.startsWith('https') || href.startsWith('//'))

    if (isExternalLink) {
      return (
        <LinkCustomExternalStyled
          href={href}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          ref={ref}
          {...rest}
        >
          {children}
          {openInNewTab ? (
            <LinkTargetDescription>{t('link.label.new_tab')}</LinkTargetDescription>
          ) : null}
        </LinkCustomExternalStyled>
      )
    }

    // For internal links, use Next.js Link with direct props
    // Note: Next.js Link doesn't forward refs to its child, so we handle it differently.
    return (
      <Link href={href} legacyBehavior passHref target={openInNewTab ? '_blank' : undefined}>
        <LinkCustomStyled ref={ref} {...rest}>
          {children}

          {openInNewTab ? (
            <LinkTargetDescription>{t('link.label.new_tab')}</LinkTargetDescription>
          ) : null}
        </LinkCustomStyled>
      </Link>
    )
  }
)

LinkCustom.displayName = 'LinkCustom'

export { LinkCustom }
